import React, { useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useEpiClergySettings, useEpiClergySettingsLoaded } from '../../hooks/useEpiClergySettings';
import { QuickSearchFormValidationSchema, ClergyQuickSearchFormValues, ClergyLocationQuickSearchResult, ClergyNameQuickSearchResult, ClergyFilters } from './clergy-model';
import { useApiResponseError } from '../../hooks/useApiError';
import { FormWrapper } from '../form-wrapper';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { QuickSearchByLocation, QuickSearchByName } from '../../ducks/clergy-api';
import { ApiSmallResponse } from '../../models/api-model';
import { replaceAll } from '../../../../TCSMApp/global/helpers/string-helpers';
import { Wysiwyg } from '../wysiwyg';
import { DelayExecute } from '../../helpers/delay';

interface Props {
    performSearch: (filters: ClergyFilters) => void;
    selectClergyById: (clergyId: string | number) => void;
    navigateAdvancedSearch: () => void;
    searchFilters: ClergyFilters;
    clearForm: () => void;
}

function ClergySearch(props: Props) {
    const { performSearch, selectClergyById, navigateAdvancedSearch, searchFilters, clearForm } = props;
    const dispatch = useDispatch();

    const clergySettingsLoaded = useEpiClergySettingsLoaded();
    const clergySettings = useEpiClergySettings();
    const apiErrors = useApiResponseError();

    const initialLocationResults: ApiSmallResponse<ClergyLocationQuickSearchResult> = {
        results: [],
        searchValue: '',
    };

    const initialNameResults: ApiSmallResponse<ClergyNameQuickSearchResult> = {
        results: [],
        searchValue: '',
    };

    const [quickSearchNameResults, setQuickSearchNameResults] = useState(initialNameResults);
    const [quickSearchNameVisible, setQuickSearchNameVisible] = useState(false);
    const [quickSearchNameInProgress, setQuickSearchNameInProgress] = useState(false);
    const [quickSearchLocationResults, setQuickSearchLocationResults] = useState(initialLocationResults);
    const [quickSearchLocationVisible, setQuickSearchLocationVisible] = useState(false);
    const [quickSearchLocationInProgress, setQuickSearchLocationInProgress] = useState(false);

    const initialValues: ClergyQuickSearchFormValues = {
        name: searchFilters?.name,
        cityOrCountry: searchFilters?.location,
    };

    function CloseAutocomplete(flagCallback: ()=> void){
        if (!flagCallback) return null;

        setTimeout(() => {
            flagCallback();
        }, 250);
    }

    function QuickSearchNameResult(searchName: string, data) {
        setQuickSearchNameResults({
            ...data,
            searchValue: searchName,
        });
        
        setQuickSearchNameInProgress(false);
    }

    function QuickSearchCityResult(searchCity: string, data) {
        setQuickSearchLocationResults(
            {
                ...data,
                searchValue: searchCity,
            });
            
        setQuickSearchLocationInProgress(false);
    }

    function SelectAllResultByName(requestSearchValue: string) {
        if (quickSearchNameResults?.results?.length > 0) {
            performSearch({ name: requestSearchValue });
        }
    }

    function SelectResultByLocation(selectedLocation: ClergyLocationQuickSearchResult) {
        if (!selectedLocation) return;

        performSearch({ location: selectedLocation.location?.trim() });
    }

    function SelectAllResultByLocation(requestSearchValue: string) {
        if (quickSearchLocationResults?.results?.length > 0) {

            performSearch({ location: requestSearchValue });
        }
    }

    function QuickSearchName(searchValue: string) {
        if (searchValue?.length > 1) {
            setQuickSearchNameVisible(true);
            setQuickSearchNameInProgress(true);
            dispatch(QuickSearchByName(searchValue, (remoteData) => QuickSearchNameResult(searchValue, remoteData)));
        }
    }

    function QuickSearchLocation(searchValue: string) {
        if (searchValue?.length > 1) {
            setQuickSearchLocationVisible(true);
            setQuickSearchLocationInProgress(true);
            dispatch(QuickSearchByLocation(searchValue, (remoteData) => QuickSearchCityResult(searchValue, remoteData)));
        }
    }

    function OnFieldChange(fieldName: string, value: string, formProps: FormikProps<ClergyQuickSearchFormValues>) {
        formProps.setFieldTouched(fieldName, true);
        formProps.setFieldValue(fieldName, value);
    }

    function OnNameChanged(e, formProps: FormikProps<ClergyQuickSearchFormValues>) {        
        OnFieldChange('name', e?.target?.value, formProps);
        DelayExecute(
            ()=>{                                 
                QuickSearchName(e?.target?.value);
            },
            'name');
    }

    function OnCityChanged(e, formProps: FormikProps<ClergyQuickSearchFormValues>) {        
        OnFieldChange('cityOrCountry', e?.target?.value, formProps);
        DelayExecute(
            ()=>{                 
                QuickSearchLocation(e?.target?.value);
            },
            'cityOrCountry');        
    }

    function OnSubmit(submitValues: ClergyQuickSearchFormValues) {
        performSearch({ name: submitValues?.name, location: submitValues?.cityOrCountry });
    }

    function OnCancel(canelFormProps: FormikProps<ClergyQuickSearchFormValues>) {
        canelFormProps.resetForm({});
        clearForm();
        setQuickSearchNameResults(initialNameResults);
        setQuickSearchLocationResults(initialLocationResults);
        window.scrollTo(0, 0);
    }

    function RenderAutocompleteNoResults(){
        return (<ul className='quickfind-autocomplete-list'>
                        <li className='quickfind-autocomplete-list-item'>
                            <div className='quickfind-autocomplete-link'>
                                <span className='quickfind-autocomplete-link-detail'>{clergySettings?.noResultsAutocompleteMessage || 'Sorry, no results were found.'}</span>
                            </div>
                        </li>
                    </ul>);
    }

    function RenderQuickSearchNameAutocomplete() {
        if (quickSearchNameVisible && !quickSearchNameInProgress) {
            if (quickSearchNameResults?.results?.length > 0){
            return (<ul className='quickfind-autocomplete-list'>
                {quickSearchNameResults.results.map(
                    (item) =>
                        <li className='quickfind-autocomplete-list-item'>
                            <button className='quickfind-autocomplete-link' type='button' onClick={() => selectClergyById(item?.guid || item?.party_id)} key={item?.guid || item?.party_id}>
                                {item.name}
                                {item?.position?.trim() && <span className='quickfind-autocomplete-link-detail'>{item?.position?.trim()}</span>}
                            </button>                            
                        </li>,
                )}

                <li className='quickfind-autocomplete-divider'>
                    <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectAllResultByName(quickSearchNameResults?.searchValue)}>{replaceAll(clergySettings?.viewAllResultsTemplate, '{0}', `${quickSearchNameResults?.total_results}`)}</button>
                </li>
                </ul>);
            }

            return (<RenderAutocompleteNoResults />);
        }

        return null;
    }

    function RenderQuickSearchLocationsAutocomplete() {
        if (quickSearchLocationVisible && !quickSearchLocationInProgress) {
            if (quickSearchLocationResults?.results?.length > 0){

                return (<ul className='quickfind-autocomplete-list'>

                {quickSearchLocationResults.results.map(
                    (item) =>
                    <li className='quickfind-autocomplete-list-item'>
                            {item?.location?.trim() && <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectResultByLocation(item)} key={item?.location?.trim()}>{item?.location?.trim()}</button>}
                        </li>,
                )}

                <li className='quickfind-autocomplete-divider'>
                    <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectAllResultByLocation(quickSearchLocationResults?.searchValue)}>{replaceAll(clergySettings?.viewAllResultsTemplate, '{0}', `${quickSearchLocationResults?.total_results}`)}</button>
                </li>
                </ul>);
            }

            return (<RenderAutocompleteNoResults />);
        }

        return null;
    }

    function FormFooter() {
        if (!clergySettings?.termsOfUseMessage) return null;

        return (
            <Wysiwyg content={clergySettings?.termsOfUseMessage} />
        );
    }

    function IsFilterEmpty(formProps: FormikProps<ClergyQuickSearchFormValues>): boolean {
        return formProps && formProps.values &&
            !formProps.values.name &&
            !formProps.values.cityOrCountry;
    }

    if (!clergySettingsLoaded || !clergySettings) {
        return <LoadingCircle />;
    }

    return (
        <>
            <h3 className='ecd-page-content-title'>
                {clergySettings.pageTitle}
            </h3>
            <div className='ecd-page-content-section'>
                <p>
                    <strong>{clergySettings.formInstructionMessage}</strong>
                </p>
                <Formik
                    initialValues={initialValues}
                    onSubmit={OnSubmit}
                    validationSchema={QuickSearchFormValidationSchema(clergySettings)}
                    enableReinitialize
                    initialErrors={apiErrors}
                >
                    {(formProps: FormikProps<ClergyQuickSearchFormValues>) => (
                        <Form>
                            <FormWrapper
                                formikProps={formProps}
                                submitDisabled={IsFilterEmpty(formProps)}
                                submitText={clergySettings.submitFormButton}
                                cancelText={clergySettings.clearFormButton}
                                onCancel={() => { OnCancel(formProps); }}
                                preventButtonDisableValidation
                                formFooter={FormFooter()}
                            >
                                <div className='form-group'>
                                    <div className=' form-item quickfind-autocomplete-container'>
                                        <FormField                                            
                                            name='name'
                                            label={clergySettings?.nameLabel || 'Enter Name'}
                                            component='input'
                                            touched={formProps.touched}
                                            errors={formProps.errors}
                                            onChange={(eventNameChanged) => OnNameChanged(eventNameChanged, formProps)}
                                            onBlur={()=> CloseAutocomplete(() => setQuickSearchNameVisible(false))}
                                        />
                                        <RenderQuickSearchNameAutocomplete />
                                    </div>
                                    <div className=' form-item quickfind-autocomplete-container'>
                                        <FormField                                            
                                            name='cityOrCountry'
                                            label={clergySettings?.cityOrCountryLabel || 'Enter City or Country'}
                                            component='input'
                                            touched={formProps.touched}
                                            errors={formProps.errors}
                                            onChange={(eventCityChanged) => OnCityChanged(eventCityChanged, formProps)}
                                            onBlur={()=> CloseAutocomplete(() => setQuickSearchLocationVisible(false))}                                            
                                        />
                                        <RenderQuickSearchLocationsAutocomplete />
                                    </div>
                                </div>
                            </FormWrapper>
                            
                            <button className='quickfind-advanced-cta' type='button' onClick={navigateAdvancedSearch}>{clergySettings.advancedSearchButton}</button>
                        </Form>
                    )}
                </Formik >
            </div>
            <div className='ecd-page-content-section'>
                <Wysiwyg content={clergySettings?.postFormMessage} />
            </div>
        </>
    );
}

export default ClergySearch;
